// AlertModal.tsx
import React, { useEffect, useState } from "react";

type AlertType = "success" | "error";

interface AlertModalProps {
  type: AlertType;
  title?: string;
  message: string;
  isOpen: boolean;
  onClose: () => void;
  autoCloseDuration?: number; // ms, optional auto-dismiss
}

const ICONS = {
  success: (
    <svg viewBox="0 0 52 52" fill="none" style={{ width: 56, height: 56 }}>
      <circle
        cx="26"
        cy="26"
        r="25"
        stroke="#22c55e"
        strokeWidth="2"
        fill="#dcfce7"
      />
      <path
        d="M14 27l8 8 16-16"
        stroke="#22c55e"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  error: (
    <svg viewBox="0 0 52 52" fill="none" style={{ width: 56, height: 56 }}>
      <circle
        cx="26"
        cy="26"
        r="25"
        stroke="#ef4444"
        strokeWidth="2"
        fill="#fee2e2"
      />
      <path
        d="M18 18l16 16M34 18L18 34"
        stroke="#ef4444"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  ),
};

const COLORS = {
  success: {
    accent: "#22c55e",
    light: "#f0fdf4",
    border: "#bbf7d0",
    button: "#16a34a",
    buttonHover: "#15803d",
    text: "#166534",
  },
  error: {
    accent: "#ef4444",
    light: "#fff1f2",
    border: "#fecdd3",
    button: "#dc2626",
    buttonHover: "#b91c1c",
    text: "#991b1b",
  },
};

const AlertModal: React.FC<AlertModalProps> = ({
  type,
  title,
  message,
  isOpen,
  onClose,
  autoCloseDuration,
}) => {
  const [visible, setVisible] = useState(false);
  const [hovering, setHovering] = useState(false);
  const colors = COLORS[type];

  // Handle open/close animation
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  // Auto-close logic
  useEffect(() => {
    if (isOpen && autoCloseDuration) {
      const timer = setTimeout(onClose, autoCloseDuration);
      return () => clearTimeout(timer);
    }
  }, [isOpen, autoCloseDuration, onClose]);

  if (!isOpen) return null;

  const defaultTitle = type === "success" ? "Éxito!" : "Algo salió mal";

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        backdropFilter: "blur(4px)",
        transition: "opacity 0.25s ease",
        opacity: visible ? 1 : 0,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.light,
          border: `1.5px solid ${colors.border}`,
          borderRadius: 20,
          padding: "40px 36px 32px",
          maxWidth: 400,
          width: "90%",
          boxShadow: `0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px ${colors.border}`,
          textAlign: "center",
          transform: visible
            ? "scale(1) translateY(0)"
            : "scale(0.85) translateY(30px)",
          transition:
            "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease",
          opacity: visible ? 1 : 0,
          position: "relative",
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 14,
            right: 16,
            background: "none",
            border: "none",
            fontSize: 22,
            cursor: "pointer",
            color: "#94a3b8",
            lineHeight: 1,
            padding: "2px 6px",
            borderRadius: 6,
            transition: "color 0.2s",
          }}
          onMouseEnter={(e) =>
            ((e.target as HTMLElement).style.color = "#475569")
          }
          onMouseLeave={(e) =>
            ((e.target as HTMLElement).style.color = "#94a3b8")
          }
          aria-label="Close"
        >
          ×
        </button>

        {/* Icon */}
        <div style={{ marginBottom: 16 }}>{ICONS[type]}</div>

        {/* Title */}
        <h2
          style={{
            margin: "0 0 10px",
            fontSize: 22,
            fontWeight: 700,
            color: colors.text,
            fontFamily: "system-ui, -apple-system, sans-serif",
          }}
        >
          {title ?? defaultTitle}
        </h2>

        {/* Message */}
        <p
          style={{
            margin: "0 0 28px",
            fontSize: 15,
            color: "#475569",
            lineHeight: 1.6,
            fontFamily: "system-ui, -apple-system, sans-serif",
          }}
        >
          {message}
        </p>

        {/* CTA Button */}
        <button
          onClick={onClose}
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          style={{
            backgroundColor: hovering ? colors.buttonHover : colors.button,
            color: "#fff",
            border: "none",
            borderRadius: 10,
            padding: "11px 32px",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
            transition: "background-color 0.2s, transform 0.1s",
            transform: hovering ? "scale(1.03)" : "scale(1)",
            fontFamily: "system-ui, -apple-system, sans-serif",
            letterSpacing: "0.02em",
          }}
        >
          {type === "success" ? "Cerrar!" : "Cerrar"}
        </button>
      </div>
    </div>
  );
};

export default AlertModal;
